import { useEffect, useLayoutEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { getCachedAuthContext } from "~/components/auth/AuthenticationProvider";
import { useAuthentication } from "~/hooks/useAuthentication";

import {
  callbackParamsFrom,
  readConnectSession,
  writeCallbackParams,
} from "./connect";
import {
  INTEGRATION_CALLBACK_PATH,
  ESIGNATURE_COMPLETE_PATH,
} from "./constants";

/**
 * `/integrations/callback` — where providers send the browser back, with
 * `?code&state` or `?error`. It is one fixed address, so it can't name an
 * organisation; but every request is scoped to the organisation the URL names,
 * which is also what sign-in settles the session on. So this moves straight
 * onto the completion route inside the organisation the connection was started
 * in, and the completion runs there.
 *
 * It renders ahead of the sign-in gate on purpose: left on an org-less path
 * until sign-in settles, the session falls back to the person's first
 * organisation. With nothing remembered, the completion route goes unprefixed
 * and the usual prefixing places it in the session's organisation, where it
 * explains that the attempt can't be finished.
 */
export function IntegrationConnectCallback() {
  const history = useHistory();
  const { search } = useLocation();
  const { setOrgId } = useAuthentication();
  const held = useRef(false);

  // Layout, so the provider's `code` and `state` are off the address bar
  // before any start-up effect reads it — the sign-in library's would take
  // them for its own (see `INTEGRATION_CALLBACK_PARAMS_KEY`). Should the
  // browser refuse to hold them, they stay: slower, through Auth0, but still
  // finished.
  useLayoutEffect(() => {
    held.current = writeCallbackParams(callbackParamsFrom(search));
    if (held.current) history.replace(INTEGRATION_CALLBACK_PATH);
  }, []);

  // Passive, because the session's setter can't be called from a layout
  // effect while its provider is still mounting.
  useEffect(() => {
    const organisationId = readConnectSession()?.organisationId;
    if (organisationId && getCachedAuthContext()?.orgId !== organisationId) {
      // As the organisation switcher does: settle the session first, or the
      // prefix guard sends the new URL straight back to the old organisation.
      setOrgId(organisationId);
    }
    const prefix = organisationId ? `/organisation/${organisationId}` : "";
    history.replace(
      `${prefix}${ESIGNATURE_COMPLETE_PATH}${held.current ? "" : search}`,
    );
  }, []);

  return null;
}

export default IntegrationConnectCallback;
